import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/fp/get';
import map from 'lodash/fp/map';

interface ExpandableUpdateEventsTableProps {
    items: any[];
    columnDescriptors: any[];
    itemKey?: string;
    className?: string;
    showHeader?: boolean;
    onRowClick?: (rowId: string, isSelected: boolean) => void;
}

const tableClassName = 'table table-hover table-column-overflow-hidden table-head-filled table-sticky';

const ExpandableUpdateEventsTable = ({
    items,
    columnDescriptors,
    itemKey = 'id',
    className,
    showHeader = true,
    onRowClick = () => {},
}: ExpandableUpdateEventsTableProps) => {
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const handleToggleRow = (id: string) => {
        if (expandedRows.includes(id)) {
            setExpandedRows(expandedRows.filter(expanded => expanded !== id));
        } else {
            setExpandedRows([...expandedRows, id]);
        }
    };

    const renderTableHead = () => {
        const tableHeaders = map((columnDescriptor) => {
            if (columnDescriptor.hidden) {
                return null;
            }
            return (
                <th key={columnDescriptor.id}>
                    {columnDescriptor.label ? <FormattedMessage id={columnDescriptor.label} /> : columnDescriptor.label}
                </th>
            );
        }, columnDescriptors);

        return (
            <thead className='table-head'>
                <tr>
                    {tableHeaders}
                    <th className='table-action' />
                </tr>
            </thead>
        );
    };

    const getIndentClass = (depth: number): string => {
        if (depth === 0) return '';
        if (depth === 1) return 'padding-left-25';
        return 'padding-left-50';
    };

    const renderTableData = (item: any, columnDescriptor: any, depth: number, isFirstColumn: boolean) => {
        if (columnDescriptor.hidden) {
            return null;
        }

        const content = columnDescriptor.format
            ? columnDescriptor.format(get(columnDescriptor.field, item), item)
            : item[columnDescriptor.field];

        return (
            <td key={`td-${columnDescriptor.id}`} className={classNames(columnDescriptor.className)}>
                {depth > 0 && isFirstColumn ? (
                    <span className={getIndentClass(depth)}>{content}</span>
                ) : content}
            </td>
        );
    };

    const renderTableRow = (item: any, depth = 0): React.ReactNode => {
        const key = item[itemKey];
        const hasSubEvents = item.subEvents && item.subEvents.length > 0;
        const isExpanded = expandedRows.includes(key);

        let firstVisibleFound = false;
        const tableData = map(columnDescriptor => {
            const isFirst = !columnDescriptor.hidden && !firstVisibleFound;
            if (isFirst) firstVisibleFound = true;
            return renderTableData(item, columnDescriptor, depth, isFirst);
        }, columnDescriptors);

        return (
            <React.Fragment key={key}>
                <tr
                    data-key={key}
                    className={depth > 0 ? 'extendedRow' : 'compactRow'}
                    onClick={() => depth === 0 && onRowClick(key, true)}
                >
                    {tableData}
                    <td className='table-action'>
                        {hasSubEvents && (
                            <span onClick={(e) => {
                                e.stopPropagation();
                                handleToggleRow(key);
                            }}>
                                <span className='btn btn-muted btn-icon-only'>
                                    <span
                                        className={classNames('uikit-icon', {
                                            'icon-chevron-up': isExpanded,
                                            'icon-chevron-down': !isExpanded,
                                        })}
                                    />
                                </span>
                            </span>
                        )}
                    </td>
                </tr>
                {hasSubEvents && isExpanded && item.subEvents.map((subEvent: any) => renderTableRow(subEvent, depth + 1))}
            </React.Fragment>
        );
    };

    const renderTableBody = () => {
        const tableRows = map((item) => renderTableRow(item), items);
        return <tbody>{tableRows}</tbody>;
    };

    if (!items) {
        return <FormattedMessage id='intl-msg:notFound' />;
    }

    return (
        <table className={classNames(tableClassName, className)}>
            {showHeader && renderTableHead()}
            {renderTableBody()}
        </table>
    );
};

export default ExpandableUpdateEventsTable;
